import React, { useState } from 'react';
import BackButton from './BackButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import searchIcon from "../../../../../assets/icons/search.svg";
import ArrowUp from "../../../../../assets/icons/arrow-up-right_Purple.svg";
import { Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { articles as articlesData, Article } from '../../../../../utils/articles';

interface ArticleCardProps {
  title: string;
  description: string;
  tags: string[];
  onViewArticle?: () => void;
}

// Inline style for multi-line truncation using textOverflow: "ellipsis"
const truncateStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3, // Shows only three lines
  WebkitBoxOrient: 'vertical',
};

const ArticleCard: React.FC<ArticleCardProps> = ({ title, description, tags, onViewArticle }) => (
  <Card className="flex flex-col p-4 dark:bg-[#1e1e1e] rounded-xl border border-[#f0eff8] dark:border-[#333]">
    <div className="flex flex-row gap-2">
      <div className="flex-1 flex flex-col gap-1">
        <h3 className="text-[16px] font-semibold">{title}</h3>
        <p style={truncateStyle} className="text-[14px] text-[#767676] dark:text-[#aaa]">
          {description}
        </p>
      </div>
      <div
        onClick={onViewArticle}
        className="p-1 rounded-lg border border-[#5547dd] w-8 h-8 flex items-center justify-center cursor-pointer"
      >
        <img src={ArrowUp} alt="arrow" className="w-6 h-6" />
      </div>
    </div>
    <div className="flex flex-row gap-1 mt-4">
      {tags.map((tag, index) => (
        <span
          key={index}
          className="w-[50px] h-[24px] flex items-center justify-center rounded-lg text-[14px] py-[4px] px-[8px] bg-[#EEEDFC] text-black"
        >
          {tag}
        </span>
      ))}
    </div>
  </Card>
);

const ArticleSectionViewAll: React.FC = () => {
  const navigate = useNavigate();
  
  // State for search term and selected tag filter
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('All');

  // Handler for search updates
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Define filter tags including "All" and the other tags.
  const filterTags = ['All', 'Tag 1', 'Tag 2', 'Tag 3', 'Tag 4', 'Tag 5'];

  // Filter articles based on the search term and the selected tag.
  const filteredArticles = articlesData.filter((article: Article) => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = selectedTag === 'All' || article.tags.includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  return (
    <div className="flex flex-col w-full">
      <BackButton />

      <div className="flex flex-col gap-4">
        {/* Title and Search Header */}
        <div className="flex flex-row items-center w-full">
          <h4 className="text-[24px] font-semibold leading-[36px]">Articles</h4>
          <div className="ml-auto">
            <TextField
              variant="outlined"
              placeholder="Search"
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img
                      src={searchIcon}
                      alt="search"
                      style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                    />
                  </InputAdornment>
                ),
                style: {
                  height: '48px',
                  paddingRight: '16px',
                  paddingLeft: '16px',
                  fontSize: '14px',
                  borderRadius: '8px',
                },
              }}
              sx={{
                width: '400px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  border: '1px solid #E0E0E0',
                  paddingRight: '8px',
                  '& fieldset': { border: 'none' },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#808080',
                  opacity: 1,
                },
              }}
            />
          </div>
        </div>

        {/* Tag Filter Row */}
        <div className="flex flex-row gap-2 overflow-x-auto">
          {filterTags.map((tag: string, index: number) => (
            <button
              key={index}
              onClick={() => setSelectedTag(tag)}
              className={`px-3 py-[4px] rounded-lg border text-[14px] ${
                tag === selectedTag
                  ? 'bg-[#eeedfc] border-[#aaa3ee] font-semibold text-black'
                  : 'bg-white border-[#e0e0e0] text-black'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        {filteredArticles.length === 0 ? (
          <div className="mt-8 text-center text-gray-500">No articles found.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {filteredArticles.map((article: Article, index: number) => (
              <ArticleCard
              key={article.id}
                {...article}
                onViewArticle={() => navigate(`/knowledgeCentre/Articles/${article.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleSectionViewAll;
